import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate, useLocation } from 'react-router-dom';
import { LOGIN_CONSTANTS } from './constants';
import logo from '../../assets/images/logo.png';
import './LoginForm.css';

const loginSchema = yup.object({
  email: yup
    .string()
    .email(LOGIN_CONSTANTS.ERROR_MESSAGES.INVALID_EMAIL)
    .required(LOGIN_CONSTANTS.ERROR_MESSAGES.REQUIRED_EMAIL),
  password: yup
    .string()
    .min(LOGIN_CONSTANTS.MIN_PASSWORD_LENGTH, LOGIN_CONSTANTS.ERROR_MESSAGES.PASSWORD_TOO_SHORT)
    .required(LOGIN_CONSTANTS.ERROR_MESSAGES.REQUIRED_PASSWORD),
});

// ─── Inline SVG icons (no extra deps) ────────────────────────────────────────

const EyeIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
    <circle cx="12" cy="12" r="3" />
  </svg>
);

const EyeOffIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94" />
    <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19" />
    <line x1="1" y1="1" x2="23" y2="23" />
  </svg>
);

const AlertIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <circle cx="12" cy="12" r="10" />
    <line x1="12" y1="8" x2="12" y2="12" />
    <line x1="12" y1="16" x2="12.01" y2="16" />
  </svg>
);

// ─── Component ────────────────────────────────────────────────────────────────

export function LoginForm() {
  const { login, error, clearError, isAuthenticated } = useAuth();
  const navigate  = useNavigate();
  const location  = useLocation();

  const [showPassword, setShowPassword] = useState(false);
  const [deviceError,  setDeviceError]  = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm({
    resolver: yupResolver(loginSchema),
    defaultValues: { email: '', password: '' },
  });

  // ── Existing auth effects (unchanged) ──────────────────────────────────────

  useEffect(() => {
    if (isAuthenticated) {
      const from = location.state?.from?.pathname || '/dashboard';
      navigate(from, { replace: true });
    }
  }, [isAuthenticated, navigate, location]);

  useEffect(() => {
    clearError();
    setDeviceError(null);
  }, []);

  useEffect(() => {
    if (error) {
      if (
        error.includes('restricted to your registered device') ||
        error.includes('device') ||
        error.includes('Device')
      ) {
        setDeviceError(error);
        toast.error(error);
      } else {
        toast.error(error);
      }
    }
  }, [error]);

  // ── Submit handler (unchanged) ─────────────────────────────────────────────

  const onSubmit = async (data) => {
    setIsSubmitting(true);
    clearError();
    setDeviceError(null);

    try {
      const result = await login(data.email, data.password);

      if (result.success) {
        reset();
        const from = location.state?.from?.pathname || '/dashboard';
        navigate(from, { replace: true });
      } else if (result.error && result.error.includes('device')) {
        setDeviceError(result.error);
      }
    } catch (_err) {
      // Error handled by auth context
    } finally {
      setIsSubmitting(false);
    }
  };

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <div className="lf-page">
      <div className="lf-card" role="main">

        {/* ── Header: logo + title ── */}
        <div className="lf-header">
          <img
            src={logo}
            alt="Application logo"
            className="lf-logo"
          />
          <h1 className="lf-title">Welcome Back</h1>
          <p className="lf-subtitle">Sign in to your account to continue</p>
        </div>

        {/* ── Body: form ── */}
        <div className="lf-body">

          {/* Device error banner */}
          {deviceError && (
            <div className="lf-device-error" role="alert">
              <span className="lf-device-error-icon"><AlertIcon /></span>
              <div>
                <strong>{LOGIN_CONSTANTS.ERROR_MESSAGES.DEVICE_RESTRICTED}</strong>
                <p>{LOGIN_CONSTANTS.ERROR_MESSAGES.DEVICE_MESSAGE}</p>
              </div>
            </div>
          )}

          <form onSubmit={handleSubmit(onSubmit)} noValidate>

            {/* User ID */}
            <div className="lf-form-group">
              <label htmlFor="email" className="lf-label">User ID</label>
              <input
                id="email"
                type="email"
                className={`lf-input${errors.email ? ' lf-input--error' : ''}`}
                placeholder="Enter your user ID"
                autoComplete="email"
                disabled={isSubmitting}
                {...register('email')}
              />
              {errors.email && (
                <span className="lf-field-error" role="alert">
                  <AlertIcon /> {errors.email.message}
                </span>
              )}
            </div>

            {/* Password */}
            <div className="lf-form-group">
              <label htmlFor="password" className="lf-label">Password</label>
              <div className="lf-password-wrap">
                <input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  className={`lf-input${errors.password ? ' lf-input--error' : ''}`}
                  placeholder="Enter your password"
                  autoComplete="current-password"
                  disabled={isSubmitting}
                  {...register('password')}
                />
                <button
                  type="button"
                  className="lf-eye-btn"
                  onClick={() => setShowPassword((v) => !v)}
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                  tabIndex={0}
                  disabled={isSubmitting}
                >
                  {showPassword ? <EyeOffIcon /> : <EyeIcon />}
                </button>
              </div>
              {errors.password && (
                <span className="lf-field-error" role="alert">
                  <AlertIcon /> {errors.password.message}
                </span>
              )}
            </div>

            {/* Submit */}
            <button
              type="submit"
              className="lf-submit-btn"
              disabled={isSubmitting}
            >
              {isSubmitting && <span className="lf-spinner" aria-hidden="true" />}
              {isSubmitting ? 'Signing in…' : 'Sign In'}
            </button>

          </form>
        </div>

        {/* ── Footer ── */}
        <div className="lf-footer">
          © {new Date().getFullYear()} PP Web Application
        </div>

      </div>
    </div>
  );
}

export default LoginForm;
